import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { CasesApiClient } from '../api/cases-api-client';
import { ResultValue } from '@@/types/ApiResult';
import { getFileTypeFromMime } from '../components/AttachmentUploader';
import type { AttachmentWithDetails } from '../types/api.types';

interface UseUploadCaseAttachmentsOptions {
  caseId?: string;
  onSuccess?: (fileCount: number) => void;
  onError?: (error: Error) => void;
}

export const useUploadCaseAttachments = ({ caseId, onSuccess, onError }: UseUploadCaseAttachmentsOptions) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (files: File[]) => {
      if (!caseId) {
        throw new Error('Case ID is required');
      }

      const client = new CasesApiClient();

      // Upload all files in parallel
      const uploadPromises = files.map(async (file) => {
        const result = await client.uploadCaseAttachment({
          caseId,
          attachment: file
        });

        if (ResultValue.error(result)) {
          throw new Error(result.message || `Failed to upload ${file.name}`);
        }

        return result.data;
      });

      await Promise.all(uploadPromises);
      return files.length;
    },
    onSuccess: async (fileCount) => {
      toast.success('Files uploaded successfully', {
        description: `${fileCount} file(s) uploaded to case`,
        duration: 3000
      });

      // Invalidate attachments cache to refetch fresh data
      await queryClient.invalidateQueries({
        queryKey: generateUseCaseAttachmentsQueryKey(caseId),
        refetchType: 'all'
      });

      onSuccess?.(fileCount);
    },
    onError: (error) => {
      const err = error instanceof Error ? error : new Error('Unknown error');
      toast.error('Failed to upload files', {
        description: err.message,
        duration: 3000
      });
      onError?.(err);
    }
  });
};

interface UseDownloadCaseAttachmentOptions {
  onSuccess?: (filename: string) => void;
  onError?: (error: Error) => void;
}

export const useDownloadCaseAttachment = ({ onSuccess, onError }: UseDownloadCaseAttachmentOptions = {}) => {
  return useMutation({
    mutationFn: async (downloadEndpoint: string) => {
      const supportIndex = downloadEndpoint.indexOf('/support');
      if (supportIndex === -1) {
        throw new Error('Invalid download endpoint');
      }

      const path = downloadEndpoint.substring(supportIndex + 1);
      const client = new CasesApiClient();
      const result = await client.downloadCaseAttachment(path);

      if (ResultValue.error(result)) {
        throw new Error(result.message || 'Failed to download attachment');
      }

      const response = result.data;
      const downloadUrl = response?.download_url || response?.data?.download_url;

      if (!downloadUrl) {
        throw new Error('Download URL not found in response');
      }

      return {
        downloadUrl,
        filename: response?.filename || 'download'
      };
    },
    onSuccess: ({ downloadUrl, filename }) => {
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success('Download started successfully');

      onSuccess?.(filename);
    },
    onError: (error) => {
      const err = error instanceof Error ? error : new Error('Unknown error');
      console.error('Download error:', err);
      toast.error('Failed to download file', {
        description: err.message
      });
      onError?.(err);
    }
  });
};

export const generateUseCaseAttachmentsQueryKey = (caseId?: string) => ['case-attachments', caseId];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const transformApiAttachment = (apiAttachment: any): AttachmentWithDetails => {
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Format date
  const formatDate = (isoString: string): string => {
    const date = new Date(isoString);
    return date.toLocaleDateString('en-US', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const fileType = getFileTypeFromMime(apiAttachment.file_type);

  return {
    id: apiAttachment.file_arn || apiAttachment.related_item_id,
    name: apiAttachment.file_name,
    kind: fileType === 'image' ? 'image' : 'file',
    fileType,
    size: formatFileSize(apiAttachment.file_size_bytes),
    url: apiAttachment.download_url,
    uploadDate: formatDate(apiAttachment.association_time),
    downloadEndpoint: apiAttachment.download_endpoint
  };
};

export const useCaseAttachments = (caseId?: string) => {
  const { data, isPending, error, refetch } = useQuery<AttachmentWithDetails[], Error>({
    queryKey: generateUseCaseAttachmentsQueryKey(caseId),
    queryFn: async () => {
      if (!caseId) {
        throw new Error('Case ID is required');
      }

      const client = new CasesApiClient();
      const result = await client.getCaseAttachments(caseId);

      if (ResultValue.error(result)) {
        throw new Error(result.message || 'Failed to fetch attachments');
      }

      const response = result.data;
      const transformedAttachments = (response.attachments || []).map(transformApiAttachment);
      return transformedAttachments;
    },
    enabled: !!caseId,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    gcTime: 1000 * 60 * 10, // Keep in cache for 10 minutes
    retry: 1,
    refetchOnWindowFocus: false
  });

  return {
    attachments: data || [],
    isLoading: isPending,
    error,
    refetch
  };
};
